import { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AIModel, Attachment, Message } from '@volt/shared';
import { formatKopecks } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import type { TranslationKey } from '../i18n/index.js';
import { copyToClipboard } from '../lib/dom.js';
import { parseQuotedMessage } from '../lib/messageQuote.js';
import { formatGenerationMetrics } from '../lib/messageMetrics.js';
import { useSession } from '../state/session.js';
import { hapticFeedback } from '../lib/haptics.js';
import { Icon } from './Icon.js';
import { ThinkingDragon } from './ThinkingDragon.js';
import { AssistantContent } from './AssistantContent.js';
import { useSmoothText } from '../hooks/useSmoothText.js';
import { IconButton } from './IconButton.js';
import { ContextMenu, type MenuItem } from './ContextMenu.js';

export interface MessageBubbleProps {
  message: Message;
  models: AIModel[];
  isStreaming?: boolean;
  isThinking?: boolean;
  /**
   * Provider-supplied processing explanation for this assistant message, shown
   * live before the answer starts. Never part of `message.content`, so it is
   * excluded from copy/share and from token accounting.
   */
  reasoning?: string | null;
  onShare?: (conversationId: string, messageId?: string) => void;
  onRegenerate?: (assistantMessageId: string, modelId: string) => void;
  onEdit?: (userMessageId: string) => void;
  /** Attach the whole message as quoted context for the next prompt. */
  onAttachContext?: (message: Message) => void;
  /** Ask to delete this message and everything after it. */
  onRewind?: (messageId: string) => void;
  /** User message that never reached the server (offline / stream failed to open). */
  sendFailed?: boolean;
  onRetrySend?: () => void;
  isChapter?: boolean;
  onToggleChapter?: (messageId: string) => void;
  onFeedback?: (messageId: string, feedback: 'up' | 'down' | null) => void;
  selectionMode?: boolean;
  selected?: boolean;
  selectionDisabled?: boolean;
  onToggleSelection?: (id: string) => void;
  /** The reply's clarifying quiz (if any) can still be answered. */
  quizAnswerable?: boolean;
  /** Sends the quiz answers as the next user message. */
  onSubmitQuiz?: (text: string) => void;
}

export interface LongPressHooks {
  /** The finger has been held long enough to show the "pressed" state. */
  onPressStart?: () => void;
  /** The press ended or was cancelled (also called right before `open`). */
  onPressEnd?: () => void;
}

export const PRESS_FEEDBACK_MS = 140;

export function createMessageLongPress(
  open: (x: number, y: number) => void,
  hasSelection = () => typeof window !== 'undefined' && Boolean(window.getSelection()?.toString()),
  hooks: LongPressHooks = {},
) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  let pressing = false;
  let start: { x: number; y: number; pointerId: number } | null = null;
  const cancel = () => {
    if (timer !== null) clearTimeout(timer);
    if (pressTimer !== null) clearTimeout(pressTimer);
    timer = null;
    pressTimer = null;
    start = null;
    if (pressing) { pressing = false; hooks.onPressEnd?.(); }
  };
  return {
    cancel,
    begin(x: number, y: number, pointerId: number) {
      cancel();
      if (hasSelection()) return;
      start = { x, y, pointerId };
      pressTimer = setTimeout(() => { pressTimer = null; pressing = true; hooks.onPressStart?.(); }, PRESS_FEEDBACK_MS);
      timer = setTimeout(() => { cancel(); if (!hasSelection()) open(x, y); }, 500);
    },
    move(x: number, y: number, pointerId: number) {
      if (start && (start.pointerId !== pointerId || Math.hypot(x - start.x, y - start.y) > 8)) cancel();
    },
  };
}

/** Try to translate an API error code, falling back to the raw string. */
function translateError(t: (key: TranslationKey) => string, code: string): string {
  const key = `error.${code}` as TranslationKey;
  const translated = t(key);
  return translated !== key ? translated : code;
}

/** Signed preview links live 15 minutes; one lookup per attachment per view is plenty. */
const ATTACHMENT_URL_TTL_MS = 10 * 60 * 1000;
const attachmentUrlCache = new Map<string, { at: number; url: Promise<string> }>();

/** Small inline preview of a photo the user attached to their message. */
function AttachmentThumb({ attachment }: { attachment: Attachment }) {
  const { api } = useSession();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let entry = attachmentUrlCache.get(attachment.id);
    if (!entry || Date.now() - entry.at > ATTACHMENT_URL_TTL_MS) {
      entry = { at: Date.now(), url: api.getAttachmentUrl(attachment.id, 'preview').then((r) => r.url) };
      attachmentUrlCache.set(attachment.id, entry);
      entry.url.catch(() => attachmentUrlCache.delete(attachment.id));
    }
    entry.url.then(
      (resolved) => {
        if (!cancelled) setUrl(resolved);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [api, attachment.id]);
  if (!url) return <span className="bubble-attachment is-loading" aria-hidden="true" />;
  return <img className="bubble-attachment" src={url} alt={attachment.fileName} loading="lazy" />;
}

/** Renders a single chat message bubble with action controls. */
export const MessageBubble = memo(MessageBubbleView);

function MessageBubbleView({
  message,
  models,
  isStreaming,
  isThinking,
  reasoning,
  onShare,
  onRegenerate,
  onEdit,
  onAttachContext,
  onRewind,
  sendFailed = false,
  onRetrySend,
  isChapter = false,
  onToggleChapter,
  onFeedback,
  selectionMode = false,
  selected = false,
  selectionDisabled = false,
  onToggleSelection,
  quizAnswerable = false,
  onSubmitQuiz,
}: MessageBubbleProps) {
  const { t } = useI18n();
  const { isAdmin } = useSession();
  const [copied, setCopied] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(true);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [keyboardMenu, setKeyboardMenu] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [lifted, setLifted] = useState<{ html: string; font: string; color: string; rect: { top: number; bottom: number; left: number; right: number; width: number; height: number } } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const suppressClickRef = useRef(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openMenuRef = useRef<(x: number, y: number) => void>(() => undefined);
  const pressRef = useRef<ReturnType<typeof createMessageLongPress> | null>(null);
  if (!pressRef.current) {
    pressRef.current = createMessageLongPress((x, y) => {
      suppressClickRef.current = true;
      setKeyboardMenu(false);
      openMenuRef.current(x, y);
    }, undefined, { onPressStart: () => setPressing(true), onPressEnd: () => setPressing(false) });
  }
  useEffect(() => {
    const cancel = () => pressRef.current?.cancel();
    const scroll = () => { cancel(); setMenuPos(null); setLifted(null); };
    const onPointerDown = (event: PointerEvent) => { if (!event.isPrimary) cancel(); };
    const onVisibilityChange = () => { if (document.hidden) scroll(); };
    const onSelectionChange = () => { if (window.getSelection()?.toString()) cancel(); };
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('blur', scroll);
    window.addEventListener('pointerup', cancel);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      cancel();
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('blur', scroll);
      window.removeEventListener('pointerup', cancel);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);
  useEffect(() => {
    if (selectionMode) { pressRef.current?.cancel(); setMenuPos(null); setLifted(null); }
  }, [selectionMode]);
  useEffect(() => {
    if (menuPos && keyboardMenu) document.querySelector<HTMLButtonElement>('.message-context-menu .menu-item')?.focus({ preventScroll: true });
  }, [menuPos, keyboardMenu]);

  const smoothContent = useSmoothText(message.content, Boolean(isStreaming) && message.role === 'assistant');
  const bubbleRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef<{ x: number; y: number; active: boolean; done: boolean } | null>(null);
  const SWIPE_REPLY_PX = 64;
  const canSwipeReply = Boolean(onAttachContext) && !selectionMode && !isStreaming && message.content.trim() !== '' && message.status !== 'failed';
  const setSwipeOffset = (dx: number) => {
    const el = bubbleRef.current;
    if (!el) return;
    el.style.transform = dx ? `translateX(${dx}px)` : '';
    el.style.setProperty('--swipe-progress', String(Math.min(1, Math.abs(dx) / SWIPE_REPLY_PX)));
  };

  // Telegram-style: the message lifts above a blurred page and the menu springs
  // out of it. A clone of the bubble is shown above the blur.
  openMenuRef.current = (x, y) => {
    const el = bubbleRef.current;
    if (el) {
      // Measure the untransformed box (the bubble may be mid "press" scale).
      const vr = el.getBoundingClientRect();
      // +2px slack: offsetWidth is rounded, and a sub-pixel shortfall would re-wrap the text.
      const w = el.offsetWidth + 2;
      const h = el.offsetHeight;
      const r = {
        left: vr.left - (w - vr.width) / 2,
        top: vr.top - (h - vr.height) / 2,
        width: w,
        height: h,
        right: vr.left - (w - vr.width) / 2 + w,
        bottom: vr.top - (h - vr.height) / 2 + h,
      };
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.transform = '';
      clone.querySelectorAll('.swipe-reply-hint').forEach((n) => n.remove());
      const cs = window.getComputedStyle(el);
      setLifted({
        html: clone.outerHTML,
        font: cs.font,
        color: cs.color,
        rect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height },
      });
    }
    setPressing(false);
    hapticFeedback('tap');
    setMenuPos({ x, y });
  };

  const wasStreamingRef = useRef(Boolean(isStreaming));
  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming && message.role === 'assistant') {
      if (message.status === 'complete') hapticFeedback('success');
      else if (message.status === 'failed') hapticFeedback('error');
    }
    wasStreamingRef.current = Boolean(isStreaming);
  }, [isStreaming, message.role, message.status]);

  const isUser = message.role === 'user';
  const isFailed = message.status === 'failed';
  const isCancelled = message.status === 'cancelled';
  const modelLabel =
    message.modelId
      ? (models.find((model) => model.id === message.modelId)?.displayName ?? message.modelId)
      : null;
  const parsed = isUser ? parseQuotedMessage(message.content) : { quote: null, body: message.content };
  const attachments = isUser ? (message.attachments ?? []) : [];
  const reasoningText = !isUser && reasoning && reasoning.trim().length > 0 ? reasoning : null;
  // Keep the existing waiting indicator until the model actually sends an
  // explanation; models without this capability never reach the branch below.
  const showWaiting = Boolean(isThinking && isStreaming) && message.content === '' && !reasoningText;
  const generationMetrics = isAdmin
    ? formatGenerationMetrics(message.generationMs, message.outputTokens)
    : null;

  async function handleCopy() {
    const ok = await copyToClipboard(message.content);
    if (ok) {
      hapticFeedback('select');
      setCopied(true);
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1500);
    }
  }

  const menuItems: MenuItem[] = [
    ...(message.content ? [{ key: 'copy', label: t('message.copy'), icon: 'copy' as const, onSelect: () => void handleCopy() }] : []),
    ...(!isUser && !isStreaming && !isFailed && !isCancelled && message.content.trim() && onShare
      ? [{ key: 'share', label: t('message.share'), icon: 'share' as const, onSelect: () => onShare(message.conversationId, message.id) }] : []),
    ...(!isUser && !isStreaming && onRegenerate && message.modelId
      ? [{ key: 'regenerate', label: t('message.regenerate'), icon: 'refresh' as const, onSelect: () => onRegenerate(message.id, message.modelId!) }] : []),
    ...(message.content.trim() && !isStreaming && onAttachContext
      ? [{ key: 'context', label: t('message.attachContext'), icon: 'attach' as const, onSelect: () => onAttachContext(message) }] : []),
    ...(message.content.trim() && !isStreaming && onToggleChapter && !message.id.startsWith('local-')
      ? [{ key: 'chapter', label: isChapter ? t('message.unpinChapter') : t('message.pinChapter'), icon: 'pin' as const, onSelect: () => onToggleChapter(message.id) }] : []),
    ...(!isStreaming && onRewind && !message.id.startsWith('local-')
      ? [{ key: 'rewind', label: t('message.rewind'), icon: 'back' as const, onSelect: () => onRewind(message.id) }] : []),
    ...(isUser && !isStreaming && onEdit
      ? [{ key: 'edit', label: t('message.edit'), icon: 'edit' as const, onSelect: () => onEdit(message.id) }] : []),
  ];

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      data-message-id={message.id}
      className={`message message-${message.role}${isChapter ? ' message-chapter' : ''}${pressing ? ' is-pressing' : ''}${lifted ? ' is-lifted' : ''}${isFailed ? ' message-failed' : ''}${selectionMode ? ' message-selectable' : ''}${selected ? ' message-selected' : ''}`}
      onContextMenu={(event) => {
        if (selectionMode || !menuItems.length) return;
        event.preventDefault();
        pressRef.current?.cancel();
        setKeyboardMenu(false);
        openMenuRef.current(event.clientX, event.clientY);
      }}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (selectionMode) {
          if ((event.key === ' ' || event.key === 'Enter') && onToggleSelection && !selectionDisabled) {
            event.preventDefault();
            hapticFeedback('select');
            onToggleSelection(message.id);
          }
          return;
        }
        if (!menuItems.length) return;
        if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
          event.preventDefault();
          pressRef.current?.cancel();
          const rect = event.currentTarget.getBoundingClientRect();
          setKeyboardMenu(true);
          openMenuRef.current(rect.left + 16, rect.bottom);
        }
      }}
      onPointerDown={(event) => {
        suppressClickRef.current = false;
        if (selectionMode || !menuItems.length || event.button !== 0 || !event.isPrimary ||
            !(event.target as HTMLElement).closest('.bubble') ||
            (event.target as HTMLElement).closest('button, a, input, textarea, summary')) return;
        pressRef.current?.begin(event.clientX, event.clientY, event.pointerId);
      }}
      onPointerMove={(event) => pressRef.current?.move(event.clientX, event.clientY, event.pointerId)}
      onPointerUp={() => pressRef.current?.cancel()}
      onPointerCancel={() => pressRef.current?.cancel()}
      onPointerLeave={() => pressRef.current?.cancel()}
      onClickCapture={(event) => {
        if (suppressClickRef.current) { event.preventDefault(); event.stopPropagation(); suppressClickRef.current = false; }
      }}
      onClick={(event) => {
        if (selectionMode && !selectionDisabled && !(event.target as HTMLElement).closest('button, a, summary'))
          { hapticFeedback('select'); onToggleSelection?.(message.id); }
      }}
    >
      {selectionMode && onToggleSelection ? (
        <button
          type="button"
          className="message-selection-toggle"
          aria-pressed={selected}
          disabled={selectionDisabled}
          aria-label={t('share.selectMessages')}
          onClick={() => { hapticFeedback('select'); onToggleSelection(message.id); }}
        >{selected ? '✓' : '○'}</button>
      ) : null}
      <div
        ref={bubbleRef}
        data-no-swipe={canSwipeReply ? '' : undefined}
        className={`bubble bubble-${message.role}${canSwipeReply ? ' bubble-swipeable' : ''}`}
        onTouchStart={(event) => {
          if (!canSwipeReply || event.touches.length !== 1) return;
          const touch = event.touches[0]!;
          swipeRef.current = { x: touch.clientX, y: touch.clientY, active: false, done: false };
        }}
        onTouchMove={(event) => {
          const swipe = swipeRef.current;
          const touch = event.touches[0];
          if (!swipe || !touch) return;
          const dx = touch.clientX - swipe.x;
          const dy = touch.clientY - swipe.y;
          if (!swipe.active) {
            if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) { swipeRef.current = null; return; }
            if (dx < -10 && Math.abs(dx) > Math.abs(dy) * 1.5 && !window.getSelection()?.toString()) {
              swipe.active = true;
              pressRef.current?.cancel();
            } else return;
          }
          const offset = Math.max(-96, Math.min(0, dx));
          setSwipeOffset(offset);
          if (!swipe.done && offset <= -SWIPE_REPLY_PX) { swipe.done = true; hapticFeedback('tap'); }
          if (swipe.done && offset > -SWIPE_REPLY_PX) swipe.done = false;
        }}
        onTouchEnd={() => {
          const swipe = swipeRef.current;
          swipeRef.current = null;
          setSwipeOffset(0);
          if (swipe?.active && swipe.done) onAttachContext?.(message);
        }}
        onTouchCancel={() => { swipeRef.current = null; setSwipeOffset(0); }}
      >
        {canSwipeReply && <span className="swipe-reply-hint" aria-hidden="true"><Icon name="back" size={16} /></span>}
        {reasoningText && (
          <details
            className="reasoning-panel"
            open={reasoningOpen}
            onToggle={(event) => setReasoningOpen((event.target as HTMLDetailsElement).open)}
          >
            <summary className="reasoning-summary">{t('message.reasoningTitle')}</summary>
            {/* aria-live is off on purpose: token-by-token updates would flood
                screen readers. The summary above announces the block. */}
            <div className="reasoning-text" aria-live="off">
              {reasoningText}
            </div>
          </details>
        )}

        {attachments.length > 0 && (
          <div className="bubble-attachments">
            {attachments.map((attachment) =>
              attachment.mime.startsWith('image/') ? (
                <AttachmentThumb key={attachment.id} attachment={attachment} />
              ) : (
                <span key={attachment.id} className="bubble-attachment-file">
                  <Icon name="attach" size={14} />
                  {attachment.fileName}
                </span>
              ),
            )}
          </div>
        )}

        {showWaiting ? (
          <ThinkingDragon />
        ) : message.content === '' && reasoningText ? null : (
          <>
            {parsed.quote && <blockquote className="bubble-quote">{parsed.quote}</blockquote>}
            {(parsed.body || !parsed.quote) && (
              isUser ? (
                <div className="bubble-content">{parsed.body}</div>
              ) : (
                <div className={`bubble-content${isStreaming ? ' is-streaming' : ''}`}>
                  <AssistantContent
                    content={smoothContent}
                    messageId={message.id}
                    isStreaming={isStreaming}
                    quizAnswerable={quizAnswerable}
                    onSubmitQuiz={onSubmitQuiz}
                  />
                </div>
              )
            )}
          </>
        )}

        {isFailed && (
          <p className="bubble-error">
            <Icon name="alert" size={14} />
            {message.errorMessage
              ? translateError(t, message.errorMessage)
              : t('message.failed')}
            {!isUser && onRegenerate && message.modelId && !isStreaming && (
              <button type="button" className="bubble-retry" onClick={() => onRegenerate(message.id, message.modelId!)}>
                {t('message.retry')}
              </button>
            )}
          </p>
        )}

        {sendFailed && (
          <p className="bubble-error">
            <Icon name="alert" size={14} />
            {t('message.notSent')}
            {onRetrySend && (
              <button type="button" className="bubble-retry" onClick={onRetrySend}>{t('message.retry')}</button>
            )}
          </p>
        )}

        {isCancelled && (
          <p className="bubble-error">{t('job.cancelled')}</p>
        )}
      </div>

      {/* Action toolbar */}
      {!selectionMode && <div className={`message-actions message-actions-${message.role}`}>
        {/* Copy */}
        {message.content && (
          <IconButton
            icon={copied ? 'check' : 'copy'}
            label={copied ? t('message.copied') : t('message.copy')}
            size={16}
            onClick={() => void handleCopy()}
          />
        )}

        {!isUser && !isStreaming && !isFailed && !isCancelled && Boolean(message.content?.trim()) && onShare && (
          <IconButton
            icon="share"
            label={t('message.share')}
            size={16}
            onClick={() => onShare(message.conversationId, message.id)}
          />
        )}

        {/* Regenerate (assistant messages) */}
        {!isUser && !isStreaming && onRegenerate && message.modelId && (
          <IconButton
            icon="refresh"
            label={t('message.regenerate')}
            size={16}
            onClick={() => onRegenerate(message.id, message.modelId!)}
          />
        )}

        {/* Edit (user messages) */}
        {isUser && !isStreaming && onEdit && (
          <IconButton
            icon="edit"
            label={t('message.edit')}
            size={16}
            onClick={() => onEdit(message.id)}
          />
        )}

        {/* Feedback (assistant messages) */}
        {!isUser && !isStreaming && onFeedback && (
          <>
            <IconButton
              icon="thumbsUp"
              label={t('message.up')}
              size={16}
              active={message.feedback === 'up'}
              onClick={() =>
                onFeedback(message.id, message.feedback === 'up' ? null : 'up')
              }
            />
            <IconButton
              icon="thumbsDown"
              label={t('message.down')}
              size={16}
              active={message.feedback === 'down'}
              onClick={() =>
                onFeedback(message.id, message.feedback === 'down' ? null : 'down')
              }
            />
          </>
        )}

        {/* Token usage */}
        {!isUser && message.outputTokens > 0 && (
          <span className="message-usage">
            {modelLabel
              ? t('message.usage', {
                  model: modelLabel,
                  input: String(message.inputTokens),
                  output: String(message.outputTokens),
                  cost: formatKopecks(message.costKopecks),
                })
              : t('message.usageNoModel', {
                  input: String(message.inputTokens),
                  output: String(message.outputTokens),
                  cost: formatKopecks(message.costKopecks),
                })}
            {generationMetrics && (
              <>
                {' '}
                · {generationMetrics}
              </>
            )}
          </span>
        )}
      </div>}
      <ContextMenu
        open={menuPos !== null}
        x={menuPos?.x ?? 0}
        y={menuPos?.y ?? 0}
        className="message-context-menu"
        items={menuItems}
        blur={lifted !== null}
        anchorRect={lifted?.rect ?? null}
        align={isUser ? 'right' : 'left'}
        onClose={() => {
          setMenuPos(null);
          setLifted(null);
          if (keyboardMenu) rootRef.current?.focus({ preventScroll: true });
        }}
      />
      {lifted && createPortal(
        <div
          className={`message message-${message.role} message-ghost`}
          aria-hidden="true"
          style={{ top: lifted.rect.top, left: lifted.rect.left, width: lifted.rect.width, height: lifted.rect.height, font: lifted.font, color: lifted.color }}
          dangerouslySetInnerHTML={{ __html: lifted.html }}
        />,
        document.body,
      )}
    </div>
  );
}
